// The load state a chip renders (resourceChipModel's `load`), read at render
// from the task's `loadBadges` map (resourceId -> peak % | null; labor and
// Space) or `stockBadges` map (resourceId -> { status, minStock, unit, date };
// Stock), and the portfolio options store, so a tier change recolours with no
// recompute. Order: option off -> null; cost-only non-labor -> null (no
// capacity, PDR-0104); stale wins; missing key -> notComputed; then
// labor/Space: null or unusable -> noCapacity, tier -> coloured, else below;
// Stock: RED -> danger (stock-out), ORANGE -> warning (covered by a later
// supply), GREEN -> below.
import i18n from "../../../../i18n";
import { getGanttChipOptions } from "./ganttChipOptions";
import { isActivityUsagesStale } from "./usagesFreshness";
import { formatLoadPct } from "./loadTiers";
import { tierFor } from "./tierFor";
import { CHIP_DIMENSIONS } from "./resourceChipModel";

const fieldOf = (record, name) => (record[name] !== undefined ? record[name] : record.get?.(name));
const STOCK_TIER = { RED: "danger", ORANGE: "warning" };

const state = (s, tooltip, pct = null, tierToken = null, badgeText = null) => ({ state: s, pct, tierToken, badgeText, tooltip });

// The call sites' translator: one stable function, so results can be cached.
export const chipT = (k, o) => i18n.t(k, o);

// Results depend only on (state, pct, language) for given options and t:
// cached per options object (a new one on every option change) and per t,
// so 400k chips share a handful of formatted objects. Callers never mutate.
const memo = new WeakMap();
const cached = (options, t, key, build) => {
  let byT = memo.get(options);
  if (!byT) memo.set(options, (byT = new WeakMap()));
  let byKey = byT.get(t);
  if (!byKey) byT.set(t, (byKey = new Map()));
  if (!byKey.has(key)) byKey.set(key, build());
  return byKey.get(key);
};

const numberFormats = new Map();
const formatQuantity = (n, unit, lang) => {
  let f = numberFormats.get(lang);
  if (!f) numberFormats.set(lang, (f = new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-US", { maximumFractionDigits: 2 })));
  return unit ? `${f.format(n)} ${unit}` : f.format(n);
};
const formatWeek = (iso, lang) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return String(iso || "");
  return new Date(y, m - 1, d).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "short", year: "numeric" });
};

// A stock has no load rate (PDR-0104): its chip says whether the projected
// inventory runs out over the activity, covered or not by a later supply.
const stockState = (badge, options, t, lang) => {
  if (!badge || typeof badge !== "object") {
    return cached(options, t, `stockNone|${lang}`, () => state("noCapacity", t("ganttContext.loadBadge.stockNoProjectionTooltip")));
  }
  const { status, minStock, unit, date } = badge;
  const quantity = Number.isFinite(minStock) ? formatQuantity(minStock, unit, lang) : "—";
  return cached(options, t, `stock|${status}|${quantity}|${date}|${lang}`, () => {
    const vars = { date: formatWeek(date, lang), minStock: quantity };
    const token = STOCK_TIER[status];
    if (!token) return state("below", t("ganttContext.loadBadge.stockOkTooltip", vars));
    const label = t(status === "RED" ? "ganttContext.loadBadge.stockOut" : "ganttContext.loadBadge.stockCovered");
    const tooltip = t(status === "RED" ? "ganttContext.loadBadge.stockOutTooltip" : "ganttContext.loadBadge.stockCoveredTooltip", vars);
    return state("tier", tooltip, null, token, label);
  });
};

export const loadStateFor = (record, resourceId, name, t, dimension) => {
  const options = getGanttChipOptions();
  if (!record || !options.loadColourEnabled) return null;
  const isStock = dimension === CHIP_DIMENSIONS.STOCK;
  if (dimension !== CHIP_DIMENSIONS.LABOR && dimension !== CHIP_DIMENSIONS.SPACE && !isStock) return null;
  const lang = i18n.resolvedLanguage || i18n.language || "en";
  if (isActivityUsagesStale(record)) {
    return cached(options, t, `stale|${lang}`, () => state("stale", t("ganttContext.loadBadge.staleTooltip")));
  }

  const badges = fieldOf(record, isStock ? "stockBadges" : "loadBadges");
  const key = String(resourceId);
  if (!badges || !Object.prototype.hasOwnProperty.call(badges, key)) {
    return cached(options, t, `notComputed|${lang}`, () => state("notComputed", t("ganttContext.loadBadge.notComputedTooltip")));
  }
  if (isStock) return stockState(badges[key], options, t, lang);
  const pct = badges[key];
  if (typeof pct !== "number" || !Number.isFinite(pct) || pct < 0) {
    return cached(options, t, `noCapacity|${lang}`, () => state("noCapacity", t("ganttContext.loadBadge.noCapacityTooltip")));
  }

  // The capacity setup check is labor's (Space capacity is the resource's own).
  const indicative = options.capacitySetupLikelyMissing && dimension === CHIP_DIMENSIONS.LABOR;
  return cached(options, t, `pct|${pct}|${indicative}|${lang}`, () => {
    const pctText = formatLoadPct(pct, lang);
    const tooltip = t("ganttContext.loadBadge.tooltip", { pct: pctText })
      + (indicative ? t("ganttContext.loadBadge.indicativeSuffix") : "");
    const tierToken = tierFor(pct, options.loadTiers);
    return tierToken ? state("tier", tooltip, pct, tierToken, pctText) : state("below", tooltip, pct);
  });
};
